import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ArrowLeft, Download, CheckCircle2, User, Edit, GitBranch, Users, Clock, ChevronDown, ChevronUp, Filter, X, LayoutGrid, GanttChart, List, Banknote, Undo2, Redo2, FileSpreadsheet, FileJson, FileText, AlertTriangle, Bot } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import TimelineComponent from '@/components/TimelineComponent';
import TaskList from '@/components/TaskList';
import ProjectEditDialog from '@/components/ProjectEditDialog';
import TaskDialog from '@/components/TaskDialog';
import KanbanBoard from '@/components/KanbanBoard';
import ProjectChat from '@/components/ProjectChat';
import ProjectNotes from '@/components/ProjectNotes';
import ActivityLog from '@/components/ActivityLog';
import ProjectTeam from '@/components/ProjectTeam';
import ProjectAttachments from '@/components/ProjectAttachments';
import ProjectResearch from '@/components/ProjectResearch';
import RiskManagement from '@/components/RiskManagement';
import useUndoRedo from '@/hooks/useUndoRedo';
import { openAIAssistantWithProject } from '@/components/AIAssistant';

const ProjectDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProjectDialogOpen, setEditProjectDialogOpen] = useState(false);
  const [criticalPath, setCriticalPath] = useState(null);
  const [showCriticalPath, setShowCriticalPath] = useState(false);
  const [employeeTimeSummary, setEmployeeTimeSummary] = useState([]);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState({});
  const [activeView, setActiveView] = useState('timeline'); // timeline, kanban, list
  const [editingTask, setEditingTask] = useState(null); // Task being edited from Kanban
  const [isCreatingTask, setIsCreatingTask] = useState(false); // For creating new tasks from Timeline/Kanban
  
  // Filter states
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterTaskStatus, setFilterTaskStatus] = useState('all');
  
  // Undo/Redo functionality
  const handleUndoRedoRefresh = useCallback(() => {
    fetchProjectDetails(true);
  }, []);
  
  const { recordAction, undo, redo, canUndo, canRedo } = useUndoRedo(
    handleUndoRedoRefresh,
    handleUndoRedoRefresh
  );
  
  // Use ref to track showCriticalPath for use in async functions
  const showCriticalPathRef = React.useRef(showCriticalPath);
  React.useEffect(() => {
    showCriticalPathRef.current = showCriticalPath;
  }, [showCriticalPath]);

  useEffect(() => {
    fetchProjectDetails();
    fetchUsers();
    fetchEmployeeTimeSummary();
  }, [id]);

  // Auto-activate critical path based on tasks extending beyond project end
  useEffect(() => {
    if (project && tasks.length > 0) {
      const projectEndDate = new Date(project.end_date);
      const hasTaskBeyondProjectEnd = tasks.some(task => {
        const taskEndDate = new Date(task.due_date);
        return taskEndDate > projectEndDate;
      });
      
      if (hasTaskBeyondProjectEnd && !showCriticalPath) {
        // Auto-activate if task extends beyond project end
        setShowCriticalPath(true);
      }
      
      // Auto-deactivate if no task extends beyond project end
      if (!hasTaskBeyondProjectEnd && showCriticalPath) {
        setShowCriticalPath(false);
      }
    }
  }, [project, tasks]);

  const fetchProjectDetails = async (refreshCriticalPath = false) => {
    // Ensure we have a valid project ID before making API calls
    if (!id) {
      console.error('No project ID provided');
      return;
    }
    
    try {
      const [projectRes, tasksRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`),
        axios.get(`${API}/projects/${id}/tasks`)
      ]);
      setProject(projectRes.data);
      setTasks(tasksRes.data);
      
      // Always fetch critical path for filter functionality
      try {
        const criticalPathRes = await axios.get(`${API}/projects/${id}/critical-path`);
        setCriticalPath(criticalPathRes.data);
      } catch (cpError) {
        console.error('Failed to fetch critical path', cpError);
      }
      
      // Refresh employee time summary
      fetchEmployeeTimeSummary();
    } catch (error) {
      console.error('Failed to fetch project details', error);
      // Only redirect on 404 (project not found) or 403 (no permission)
      // Don't redirect on network errors or other transient issues
      if (error.response?.status === 404 || error.response?.status === 403) {
        toast.error('Projekt nicht gefunden oder keine Berechtigung');
        navigate('/projects');
      } else {
        toast.error('Fehler beim Laden des Projekts. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  };

  const fetchEmployeeTimeSummary = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/time-summary-by-employee`);
      setEmployeeTimeSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch employee time summary', error);
    }
  };

  const toggleEmployeeDetails = (userId) => {
    setShowEmployeeDetails(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const formatHours = (hours) => {
    const totalSeconds = Math.round(hours * 3600);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    
    if (h === 0 && m === 0) return `${s}s`;
    if (h === 0) return `${m}m ${s}s`;
    if (m === 0 && s === 0) return `${h}h`;
    return `${h}h ${m}m ${s}s`;
  };

  // Update task from Kanban board
  const handleKanbanUpdateTask = async (taskId, updates, skipUndo = false) => {
    // Save previous state for undo
    const task = tasks.find(t => t.id === taskId);
    const previousState = task ? {
      status: task.status,
      completed: task.completed
    } : null;
    
    try {
      await axios.put(`${API}/tasks/${taskId}`, updates);
      fetchProjectDetails();
      
      // Record undo action for status changes
      if (!skipUndo && previousState && task) {
        const getColumnName = (status, completed) => {
          if (completed) return 'Erledigt';
          if (status === 'review') return 'Review';
          if (status === 'in_progress') return 'In Arbeit';
          return 'Backlog';
        };
        const newColumnName = getColumnName(updates.status, updates.completed);
        
        recordAction({
          type: 'kanban_move',
          description: `"${task.title}" nach "${newColumnName}"`,
          undo: async () => {
            await axios.put(`${API}/tasks/${taskId}`, previousState);
          },
          redo: async () => {
            await axios.put(`${API}/tasks/${taskId}`, updates);
          }
        });
      }
    } catch (error) {
      throw error;
    }
  };

  // Start timer for a task
  const handleStartTimer = async (taskId) => {
    try {
      await axios.post(`${API}/time-entries/start?task_id=${taskId}`);
      toast.success('Timer gestartet');
      // Navigate to time tracking or show indicator
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Starten des Timers');
    }
  };

  // Filter tasks based on selected filters - use useMemo to recalculate when dependencies change
  const filteredTasks = React.useMemo(() => {
    const now = new Date();
    const criticalTaskIds = criticalPath?.critical_path_task_ids || [];
    const overdueTaskIds = criticalPath?.overdue_task_ids || [];
    
    return tasks.filter(task => {
      // Filter by assignee
      if (filterAssignee !== 'all' && task.assigned_to_id !== filterAssignee) {
        return false;
      }
      
      // Filter by status
      if (filterTaskStatus !== 'all') {
        if (filterTaskStatus === 'done' && !task.completed) {
          return false;
        }
        if (filterTaskStatus === 'open' && task.completed) {
          return false;
        }
        if (filterTaskStatus === 'overdue') {
          // Check if task is overdue (due_date passed and not completed)
          const isOverdue = overdueTaskIds.includes(task.id) || 
            (!task.completed && task.due_date && new Date(task.due_date) < now);
          if (!isOverdue) {
            return false;
          }
        }
        if (filterTaskStatus === 'critical') {
          // Check if task is on critical path
          if (!criticalTaskIds.includes(task.id)) {
            return false;
          }
        }
      }
      
      return true;
    });
  }, [tasks, filterAssignee, filterTaskStatus, criticalPath]);

  const hasActiveFilters = filterAssignee !== 'all' || filterTaskStatus !== 'all';

  const clearFilters = () => {
    setFilterAssignee('all');
    setFilterTaskStatus('all');
  };

  // Check if current user can see all employee hours (project owner, admin, super_admin, or projektleiter)
  const isProjectOwner = project?.owner_id === user?.id;
  const canViewAllEmployeeHours = isProjectOwner || user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'projektleiter';

  const fetchCriticalPath = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/critical-path`);
      setCriticalPath(response.data);
    } catch (error) {
      console.error('Failed to fetch critical path', error);
      toast.error('Fehler beim Berechnen des kritischen Pfads');
    }
  };

  const toggleCriticalPath = () => {
    setShowCriticalPath(!showCriticalPath);
    if (!showCriticalPath && !criticalPath) {
      fetchCriticalPath();
    }
  };

  const handleCompleteProject = async () => {
    if (!window.confirm('Möchten Sie dieses Projekt wirklich als abgeschlossen markieren?')) return;
    try {
      await axios.put(`${API}/projects/${id}/status?status=completed`);
      toast.success('Projekt als abgeschlossen markiert!');
      fetchProjectDetails();
    } catch (error) {
      toast.error('Fehler beim Abschließen des Projekts');
    }
  };

  const handleReopenProject = async () => {
    try {
      await axios.put(`${API}/projects/${id}/status?status=active`);
      toast.success('Projekt wieder geöffnet!');
      fetchProjectDetails();
    } catch (error) {
      toast.error('Fehler beim Öffnen des Projekts');
    }
  };

  const handleExportPDF = async () => {
    try {
      // Include filters in PDF export
      const params = new URLSearchParams();
      if (filterAssignee !== 'all') params.append('filter_assignee', filterAssignee);
      if (filterTaskStatus !== 'all') params.append('filter_status', filterTaskStatus);
      
      const response = await axios.get(`${API}/export/project/${id}/pdf?${params.toString()}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Projektbericht_${project.name}_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF erfolgreich heruntergeladen!');
    } catch (error) {
      toast.error('Fehler beim Exportieren des PDFs');
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filterAssignee !== 'all') params.append('filter_assignee', filterAssignee);
      if (filterTaskStatus !== 'all') params.append('filter_status', filterTaskStatus);
      
      const response = await axios.get(`${API}/projects/${id}/export/excel?${params.toString()}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `project_${project.name}_report.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel erfolgreich heruntergeladen!');
    } catch (error) {
      toast.error('Fehler beim Exportieren der Excel-Datei');
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filterAssignee !== 'all') params.append('filter_assignee', filterAssignee);
      if (filterTaskStatus !== 'all') params.append('filter_status', filterTaskStatus);
      
      const response = await axios.get(`${API}/projects/${id}/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `project_${project.name}_tasks.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV erfolgreich heruntergeladen!');
    } catch (error) {
      toast.error('Fehler beim Exportieren der CSV-Datei');
    }
  };

  const handleExportJSON = async () => {
    try {
      const params = new URLSearchParams();
      if (filterAssignee !== 'all') params.append('filter_assignee', filterAssignee);
      if (filterTaskStatus !== 'all') params.append('filter_status', filterTaskStatus);
      
      const response = await axios.get(`${API}/projects/${id}/export/json?${params.toString()}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `project_${project.name}_export.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('JSON erfolgreich heruntergeladen!');
    } catch (error) {
      toast.error('Fehler beim Exportieren der JSON-Datei');
    }
  };

  const calculateProgress = () => {
    if (tasks.length === 0) return 0;
    const completedTasks = tasks.filter(t => t.completed).length;
    return Math.round((completedTasks / tasks.length) * 100);
  };

  // Handler for drag & drop task update (horizontal - timeline)
  const handleTaskDateUpdate = async (taskId, updates, skipUndo = false) => {
    // Find the task to save its previous state for undo
    const task = tasks.find(t => t.id === taskId);
    const previousState = task ? {
      start_date: task.start_date,
      due_date: task.due_date,
      status: task.status
    } : null;
    
    try {
      console.log('Updating task:', taskId, updates);
      await axios.put(`${API}/tasks/${taskId}`, updates);
      // Fetch new task data first
      const [projectRes, tasksRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`),
        axios.get(`${API}/projects/${id}/tasks`)
      ]);
      setProject(projectRes.data);
      setTasks(tasksRes.data);
      
      // ALWAYS recalculate critical path after task move (no condition)
      console.log('Fetching critical path...');
      const criticalPathRes = await axios.get(`${API}/projects/${id}/critical-path?t=${Date.now()}`);
      console.log('Critical path response:', criticalPathRes.data);
      setCriticalPath(criticalPathRes.data);
      
      // Auto-hide critical path panel if no critical tasks
      if (criticalPathRes.data.critical_path_task_ids.length === 0) {
        setShowCriticalPath(false);
        showCriticalPathRef.current = false;
      }
      console.log('Critical path updated');
      
      // Record undo action (only if not already undoing/redoing)
      if (!skipUndo && previousState && task) {
        recordAction({
          type: 'task_date_update',
          description: `"${task.title}" verschoben`,
          undo: async () => {
            await axios.put(`${API}/tasks/${taskId}`, previousState);
          },
          redo: async () => {
            await axios.put(`${API}/tasks/${taskId}`, updates);
          }
        });
      }
    } catch (error) {
      console.error('Failed to update task dates', error);
      throw error;
    }
  };

  // Handler for task reordering (vertical)
  const handleTaskReorder = async (taskOrders, skipUndo = false) => {
    // Save previous order for undo
    const previousOrders = tasks.map((t, idx) => ({ id: t.id, order: t.order || idx }));
    
    try {
      await axios.put(`${API}/projects/${id}/tasks/reorder`, { task_orders: taskOrders });
      await fetchProjectDetails(true);
      
      // Record undo action
      if (!skipUndo) {
        recordAction({
          type: 'task_reorder',
          description: 'Task-Reihenfolge geändert',
          undo: async () => {
            await axios.put(`${API}/projects/${id}/tasks/reorder`, { task_orders: previousOrders });
          },
          redo: async () => {
            await axios.put(`${API}/projects/${id}/tasks/reorder`, { task_orders: taskOrders });
          }
        });
      }
    } catch (error) {
      console.error('Failed to reorder tasks', error);
      throw error;
    }
  };

  // Handler for task dependency update
  const handleTaskDependencyUpdate = async (taskId, dependsOn, skipUndo = false) => {
    // Save previous dependencies for undo
    const task = tasks.find(t => t.id === taskId);
    const previousDependsOn = task?.depends_on || [];
    
    try {
      await axios.put(`${API}/tasks/${taskId}`, { depends_on: dependsOn });
      // Fetch new task data first
      const [projectRes, tasksRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`),
        axios.get(`${API}/projects/${id}/tasks`)
      ]);
      setProject(projectRes.data);
      setTasks(tasksRes.data);
      
      // ALWAYS recalculate critical path after dependency change (no condition)
      const criticalPathRes = await axios.get(`${API}/projects/${id}/critical-path?t=${Date.now()}`);
      setCriticalPath(criticalPathRes.data);
      
      // Record undo action
      if (!skipUndo && task) {
        recordAction({
          type: 'task_dependency_update',
          description: `Abhängigkeit für "${task.title}" geändert`,
          undo: async () => {
            await axios.put(`${API}/tasks/${taskId}`, { depends_on: previousDependsOn });
          },
          redo: async () => {
            await axios.put(`${API}/tasks/${taskId}`, { depends_on: dependsOn });
          }
        });
      }
    } catch (error) {
      console.error('Failed to update task dependencies', error);
      throw error;
    }
  };

  if (loading || !project) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-xl font-medium">Laden...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      {/* Closed Project Banner */}
      {project?.status === 'completed' && (
        <div className="mb-6 p-4 bg-muted border-2 border-border rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-muted-foreground/20 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Projekt abgeschlossen</h3>
              <p className="text-sm text-muted-foreground">Dieses Projekt ist abgeschlossen. Keine Bearbeitungen möglich.</p>
            </div>
          </div>
          <Button onClick={handleReopenProject} data-testid="reopen-project-button-banner">
            Projekt wieder öffnen
          </Button>
        </div>
      )}

      {/* Header Section - Full Width */}
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/projects')} data-testid="back-to-projects-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück
          </Button>
        </div>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="project-detail-title">
                {project?.name}
              </h1>
              <span className={`text-sm px-3 py-1 rounded-full flex items-center font-medium ${
                project?.status === 'active' ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-muted text-muted-foreground border-2 border-muted-foreground/50'
              }`}>
                {project?.status === 'active' ? 'Aktiv' : 'Abgeschlossen'}
              </span>
              {project?.status === 'active' && (
                <Button variant="ghost" size="icon" onClick={() => setEditProjectDialogOpen(true)} data-testid="edit-project-button">
                  <Edit className="w-5 h-5" />
                </Button>
              )}
            </div>
            <p className="text-muted-foreground mt-2">{project?.description}</p>
            <p className="text-sm text-muted-foreground mt-1">
              <User className="w-3 h-3 inline mr-1" />
              Projektleiter: {project?.owner_name}
            </p>
          </div>
          <div className="flex gap-2">
            {/* AI Assistant Button */}
            <Button 
              variant="outline" 
              onClick={() => openAIAssistantWithProject(project?.id, project?.name)}
              data-testid="ai-assistant-project-button"
              className="text-primary border-primary/30 hover:bg-primary/10"
            >
              <Bot className="w-4 h-4 mr-2" />
              KI-Assistent
            </Button>
            {/* Only show Critical Path button if there is a critical path */}
            {criticalPath && criticalPath.critical_path_task_ids && criticalPath.critical_path_task_ids.length > 0 && (
              <Button 
                variant={showCriticalPath ? "default" : "outline"} 
                onClick={toggleCriticalPath} 
                data-testid="critical-path-button"
              >
                <GitBranch className="w-4 h-4 mr-2" />
                Kritischer Pfad
              </Button>
            )}
            
            {/* Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-dropdown-button">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Format wählen</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleExportPDF} data-testid="export-pdf-option">
                  <FileText className="w-4 h-4 mr-2 text-red-500" />
                  PDF Bericht
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportExcel} data-testid="export-excel-option">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-green-500" />
                  Excel (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportCSV} data-testid="export-csv-option">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-blue-500" />
                  CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportJSON} data-testid="export-json-option">
                  <FileJson className="w-4 h-4 mr-2 text-yellow-500" />
                  JSON
                </DropdownMenuItem>
                {(filterAssignee !== 'all' || filterTaskStatus !== 'all') && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      <Filter className="w-3 h-3 inline mr-1" />
                      Filter werden angewendet
                    </DropdownMenuLabel>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Main Layout: Content + Sidebar - Below Header */}
      <div className="flex gap-6 mt-6 items-start relative">
        {/* Main Content Area */}
        <div className="flex-1 min-w-0 space-y-6">
        
        {/* Critical Path Info Card */}
        {showCriticalPath && criticalPath && criticalPath.critical_path_task_ids.length > 0 && (
          <Card className="border-red-500/50 bg-red-500/10 dark:bg-red-500/20" data-testid="critical-path-info-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-red-600 dark:text-red-400 flex items-center gap-2">
                <GitBranch className="w-4 h-4" />
                Kritischer Pfad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Projektdauer:</span>
                  <span className="ml-2 font-semibold text-foreground">{criticalPath.project_duration_days} Tage</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Frühestes Ende:</span>
                  <span className="ml-2 font-semibold text-foreground">{new Date(criticalPath.earliest_end_date).toLocaleDateString('de-CH')}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Kritische Tasks:</span>
                  <span className="ml-2 font-semibold text-red-600 dark:text-red-400">{criticalPath.critical_path_task_ids.length} von {tasks.length}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Tasks auf dem kritischen Pfad sind rot hervorgehoben. Jede Verzögerung dieser Tasks verlängert das Projekt.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Filter Section */}
        <Card className="bg-muted/30" data-testid="project-filter-section">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filter:</span>
              </div>
              
              {/* Assignee Filter */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Zugewiesen an:</span>
                <Select value={filterAssignee} onValueChange={setFilterAssignee}>
                  <SelectTrigger className="w-[180px] h-8 text-sm" data-testid="filter-assignee">
                    <SelectValue placeholder="Alle Personen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Personen</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Task Status Filter */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Task-Status:</span>
                <Select value={filterTaskStatus} onValueChange={setFilterTaskStatus}>
                  <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="filter-task-status">
                    <SelectValue placeholder="Alle Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Status</SelectItem>
                    <SelectItem value="open">Offen</SelectItem>
                    <SelectItem value="done">Erledigt</SelectItem>
                    <SelectItem value="overdue">Überfällig</SelectItem>
                    <SelectItem value="critical">Kritisch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearFilters}
                  className="h-8 text-xs"
                  data-testid="clear-project-filters-button"
                >
                  <X className="w-3 h-3 mr-1" />
                  Filter zurücksetzen
                </Button>
              )}
              
              {/* Filter Results Count */}
              {hasActiveFilters && (
                <span className="text-xs text-muted-foreground ml-auto">
                  {filteredTasks.length} von {tasks.length} Tasks
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card data-testid="project-progress-card">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Fortschritt</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {calculateProgress()}%
              </div>
              <div className="w-full bg-muted rounded-full h-2 mt-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${calculateProgress()}%` }}
                />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="project-tasks-count-card">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Tasks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {tasks.filter(t => t.completed).length}/{tasks.length}
              </div>
              <p className="text-xs text-muted-foreground mt-1">abgeschlossen</p>
            </CardContent>
          </Card>

          <Card data-testid="project-planned-cost-card">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Geplante Kosten</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                CHF {project?.planned_cost?.toLocaleString('de-CH', { minimumFractionDigits: 2 })}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="project-actual-cost-card">
            <CardHeader>
              <CardTitle className="text-sm font-medium">
                Effektive Kosten
                {project?.include_time_costs === false && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">(ohne Zeitkosten)</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${
                project?.actual_cost > project?.planned_cost ? 'text-destructive' : ''
              }`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                CHF {project?.actual_cost?.toLocaleString('de-CH', { minimumFractionDigits: 2 })}
              </div>
              {project?.actual_cost > project?.planned_cost && (
                <p className="text-xs text-destructive mt-1">
                  +CHF {(project.actual_cost - project.planned_cost).toLocaleString('de-CH', { minimumFractionDigits: 2 })} über Budget
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* View Tabs: Timeline, Kanban, List */}
        <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="timeline" className="flex items-center gap-2" data-testid="view-tab-timeline">
                <GanttChart className="w-4 h-4" />
                Timeline
              </TabsTrigger>
              <TabsTrigger value="kanban" className="flex items-center gap-2" data-testid="view-tab-kanban">
                <LayoutGrid className="w-4 h-4" />
                Kanban
              </TabsTrigger>
              <TabsTrigger value="list" className="flex items-center gap-2" data-testid="view-tab-list">
                <List className="w-4 h-4" />
                Liste
              </TabsTrigger>
              <TabsTrigger value="risks" className="flex items-center gap-2" data-testid="view-tab-risks">
                <AlertTriangle className="w-4 h-4" />
                Risiken
              </TabsTrigger>
            </TabsList>
            
            {/* Undo/Redo Buttons */}
            {project?.status !== 'completed' && (
              <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={undo}
                  disabled={!canUndo}
                  title="Rückgängig (Ctrl+Z)"
                  data-testid="undo-button"
                  className="h-8 w-8 p-0"
                >
                  <Undo2 className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={redo}
                  disabled={!canRedo}
                  title="Wiederherstellen (Ctrl+Shift+Z)"
                  data-testid="redo-button"
                  className="h-8 w-8 p-0"
                >
                  <Redo2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          <TabsContent value="timeline" className="mt-0">
            <TimelineComponent 
              project={project} 
              tasks={filteredTasks} 
              onTaskUpdate={handleTaskDateUpdate}
              onTaskReorder={handleTaskReorder}
              onTaskDependencyUpdate={handleTaskDependencyUpdate}
              onCreateTask={() => setIsCreatingTask(true)}
              isCompleted={project?.status === 'completed'}
              criticalPathTaskIds={criticalPath ? criticalPath.critical_path_task_ids : []}
              showCriticalPath={showCriticalPath}
            />
          </TabsContent>

          <TabsContent value="kanban" className="mt-0">
            <KanbanBoard
              tasks={filteredTasks}
              onUpdateTask={handleKanbanUpdateTask}
              onEditTask={(task) => setEditingTask(task)}
              onCreateTask={() => setIsCreatingTask(true)}
              onStartTimer={handleStartTimer}
              criticalPath={criticalPath?.critical_path_task_ids || []}
              projectCompleted={project?.status === 'completed'}
            />
          </TabsContent>

          <TabsContent value="list" className="mt-0">
            <TaskList 
              projectId={id} 
              tasks={filteredTasks} 
              users={users} 
              onTasksChange={fetchProjectDetails}
              isProjectCompleted={project?.status === 'completed'}
              currentUserId={user?.id}
            />
          </TabsContent>

          <TabsContent value="risks" className="mt-0">
            <RiskManagement 
              projectId={id}
              projectName={project?.name}
              users={users}
            />
          </TabsContent>
        </Tabs>

        {/* Cost Breakdown Section */}
        <Card data-testid="cost-breakdown-card">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Banknote className="w-4 h-4" />
              Kostenübersicht
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cost per Task */}
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">Kosten pro Task</h4>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                  {tasks.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Keine Tasks vorhanden</p>
                  ) : (
                    [...tasks]
                      .sort((a, b) => (b.actual_cost || 0) - (a.actual_cost || 0))
                      .map((task) => {
                        const percentage = task.planned_cost > 0 
                          ? Math.round((task.actual_cost || 0) / task.planned_cost * 100) 
                          : 0;
                        const isOverBudget = (task.actual_cost || 0) > task.planned_cost;
                        return (
                          <div key={task.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium truncate pr-2">{task.title}</span>
                                <span className={`text-xs font-mono ${isOverBudget ? 'text-destructive' : 'text-muted-foreground'}`}>
                                  {percentage}%
                                </span>
                              </div>
                              <div className="w-full bg-muted rounded-full h-1.5">
                                <div
                                  className={`h-1.5 rounded-full transition-all ${
                                    isOverBudget ? 'bg-destructive' : task.completed ? 'bg-green-500' : 'bg-primary'
                                  }`}
                                  style={{ width: `${Math.min(percentage, 100)}%` }}
                                />
                              </div>
                              <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                                <span>CHF {(task.actual_cost || 0).toLocaleString('de-CH', { minimumFractionDigits: 2 })}</span>
                                <span>/ CHF {task.planned_cost.toLocaleString('de-CH', { minimumFractionDigits: 2 })}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                  )}
                </div>
                {tasks.length > 0 && (
                  <div className="mt-3 pt-3 border-t flex justify-between text-sm">
                    <span className="font-medium">Gesamt Tasks</span>
                    <div className="text-right font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      <span className={project?.actual_cost > project?.planned_cost ? 'text-destructive font-bold' : ''}>
                        CHF {(project?.actual_cost || 0).toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                      </span>
                      <span className="text-muted-foreground"> / CHF {(project?.planned_cost || 0).toLocaleString('de-CH', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Cost per Employee */}
              <div>
                <h4 className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">Kosten pro Mitarbeiter</h4>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                  {employeeTimeSummary.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Keine Zeiteinträge vorhanden</p>
                  ) : (
                    [...employeeTimeSummary]
                      .sort((a, b) => (b.total_cost || 0) - (a.total_cost || 0))
                      .map((employee) => {
                        const totalProjectCost = employeeTimeSummary.reduce((sum, e) => sum + (e.total_cost || 0), 0);
                        const percentage = totalProjectCost > 0 
                          ? Math.round((employee.total_cost || 0) / totalProjectCost * 100) 
                          : 0;
                        return (
                          <div key={employee.user_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm shrink-0">
                              {employee.user_name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium truncate pr-2">{employee.user_name}</span>
                                <span className="text-xs text-muted-foreground">{percentage}%</span>
                              </div>
                              <div className="w-full bg-muted rounded-full h-1.5">
                                <div
                                  className="h-1.5 rounded-full bg-primary transition-all"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                                <span>{formatHours(employee.total_hours)}</span>
                                <span className="font-mono">CHF {(employee.total_cost || 0).toLocaleString('de-CH', { minimumFractionDigits: 2 })}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                  )}
                </div>
                {employeeTimeSummary.length > 0 && (
                  <div className="mt-3 pt-3 border-t flex justify-between text-sm">
                    <span className="font-medium">Gesamt Arbeitszeit</span>
                    <div className="text-right font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      <span>{formatHours(employeeTimeSummary.reduce((sum, e) => sum + e.total_hours, 0))}</span>
                      <span className="text-muted-foreground ml-2">
                        = CHF {employeeTimeSummary.reduce((sum, e) => sum + (e.total_cost || 0), 0).toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Employee Time Summary - Visible to project owner, admin, and projektleiter */}
        {canViewAllEmployeeHours && employeeTimeSummary.length > 0 && (
          <Card data-testid="employee-time-summary-card">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                Stundenübersicht pro Mitarbeiter
                <span className="text-xs font-normal text-muted-foreground ml-2">(für Projektleiter und Admins sichtbar)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {employeeTimeSummary.map((employee) => (
                  <div key={employee.user_id} className="border rounded-lg overflow-hidden">
                    {/* Employee Header Row */}
                    <div 
                      className="flex items-center justify-between p-3 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => toggleEmployeeDetails(employee.user_id)}
                      data-testid={`employee-row-${employee.user_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                          {employee.user_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div className="font-medium text-sm">{employee.user_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {employee.task_breakdown?.length || 0} Task{employee.task_breakdown?.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="font-mono font-bold text-lg" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {formatHours(employee.total_hours)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            CHF {employee.total_cost?.toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                          </div>
                        </div>
                        {showEmployeeDetails[employee.user_id] 
                          ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                          : <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        }
                      </div>
                    </div>
                    
                    {/* Task Breakdown (expandable) */}
                    {showEmployeeDetails[employee.user_id] && employee.task_breakdown?.length > 0 && (
                      <div className="border-t bg-background">
                        <div className="p-2 space-y-1">
                          {employee.task_breakdown.map((task, idx) => (
                            <div 
                              key={task.task_id} 
                              className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/30 text-sm"
                            >
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                <span className="truncate max-w-[200px]">{task.task_title}</span>
                              </div>
                              <div className="font-mono text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                {formatHours(task.hours)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Total Summary */}
                <div className="pt-3 border-t mt-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">Gesamt</span>
                    <div className="text-right">
                      <span className="font-mono font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {formatHours(employeeTimeSummary.reduce((sum, e) => sum + e.total_hours, 0))}
                      </span>
                      <span className="text-muted-foreground ml-3">
                        CHF {employeeTimeSummary.reduce((sum, e) => sum + e.total_cost, 0).toLocaleString('de-CH', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Project Dialog */}
        <ProjectEditDialog
          open={editProjectDialogOpen}
          onOpenChange={setEditProjectDialogOpen}
          project={project}
          onSuccess={() => {
            setEditProjectDialogOpen(false);
            fetchProjectDetails();
          }}
          onDelete={() => {
            navigate('/');
          }}
        />

        {/* Task Edit Dialog (from Kanban) */}
        {editingTask && (
          <TaskDialog
            open={!!editingTask}
            onOpenChange={(open) => !open && setEditingTask(null)}
            projectId={id}
            task={editingTask}
            tasks={tasks}
            users={users}
            mode="edit"
            currentUserId={user?.id}
            onSuccess={() => {
              setEditingTask(null);
              fetchProjectDetails();
            }}
          />
        )}

        {/* Task Create Dialog (from Timeline/Kanban) */}
        {isCreatingTask && (
          <TaskDialog
            open={isCreatingTask}
            onOpenChange={(open) => !open && setIsCreatingTask(false)}
            projectId={id}
            tasks={tasks}
            users={users}
            mode="create"
            currentUserId={user?.id}
            onSuccess={() => {
              setIsCreatingTask(false);
              fetchProjectDetails();
            }}
          />
        )}
        </div>

        {/* Right Sidebar - Collaboration Tools (Sticky) */}
        <div className="w-80 shrink-0 hidden lg:block self-start sticky top-[72px]">
          <div className="space-y-3 max-h-[calc(100vh-6rem)] overflow-y-auto pr-1 pb-4">
            <ProjectTeam projectId={id} />
            <ProjectChat projectId={id} currentUserId={user?.id} />
            <ProjectNotes projectId={id} tasks={tasks} />
            <ProjectResearch projectId={id} />
            <ProjectAttachments projectId={id} tasks={tasks} />
            <ActivityLog projectId={id} />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProjectDetail;
